import { useState, useMemo, useCallback } from 'react'
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Badge,
  Spinner,
  Row,
  Col,
  ListGroup,
  Modal,
  InputGroup
} from 'react-bootstrap'
import { useProfile } from '../contexts/ProfileContext'
import api from '../services/api'
import {
  parseBookmarksHtml,
  flattenBookmarks,
  searchBookmarks,
  getBookmarkStats,
  extractDomain
} from '../utils/bookmarkParser'

function BookmarkImport() {
  const { activeProfile } = useProfile()

  // File and parsing state
  const [bookmarkTree, setBookmarkTree] = useState(null)
  const [flatBookmarks, setFlatBookmarks] = useState([])
  const [parseError, setParseError] = useState(null)
  const [stats, setStats] = useState(null)

  // UI state
  const [viewMode, setViewMode] = useState('tree') // 'tree' or 'search'
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedFolders, setExpandedFolders] = useState(new Set(['root']))

  // Selection state
  const [selectedBookmarks, setSelectedBookmarks] = useState(new Set())

  // Curate modal state
  const [showCurateModal, setShowCurateModal] = useState(false)
  const [curateScore, setCurateScore] = useState(75)
  const [curateMinAge, setCurateMinAge] = useState('')
  const [curateMaxAge, setCurateMaxAge] = useState('')
  const [curateKeywords, setCurateKeywords] = useState('')
  const [curateLoading, setCurateLoading] = useState(false)
  const [curateProgress, setCurateProgress] = useState({ current: 0, total: 0 })
  const [curateResults, setCurateResults] = useState({ success: [], failed: [] })

  // Alert state
  const [alert, setAlert] = useState(null)

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    setParseError(null)
    setBookmarkTree(null)
    setFlatBookmarks([])
    setSelectedBookmarks(new Set())
    setStats(null)

    try {
      const content = await file.text()
      const tree = parseBookmarksHtml(content)
      const flat = flattenBookmarks(tree)
      const bookmarkStats = getBookmarkStats(tree)

      setBookmarkTree(tree)
      setFlatBookmarks(flat)
      setStats(bookmarkStats)
      setExpandedFolders(new Set(['root']))
    } catch (err) {
      setParseError(err.message || 'Failed to parse bookmarks file')
    }
  }

  // Toggle folder expansion
  const toggleFolder = useCallback((folderId) => {
    setExpandedFolders(prev => {
      const next = new Set(prev)
      if (next.has(folderId)) {
        next.delete(folderId)
      } else {
        next.add(folderId)
      }
      return next
    })
  }, [])

  // Toggle bookmark selection
  const toggleSelection = useCallback((bookmarkId) => {
    setSelectedBookmarks(prev => {
      const next = new Set(prev)
      if (next.has(bookmarkId)) {
        next.delete(bookmarkId)
      } else {
        next.add(bookmarkId)
      }
      return next
    })
  }, [])

  // Select all visible bookmarks
  const selectAllVisible = useCallback(() => {
    const visible = viewMode === 'search' && searchQuery
      ? searchBookmarks(flatBookmarks, searchQuery)
      : flatBookmarks
    setSelectedBookmarks(new Set(visible.map(b => b.id)))
  }, [flatBookmarks, viewMode, searchQuery])

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedBookmarks(new Set())
  }, [])

  // Get selected bookmark objects
  const selectedBookmarkObjects = useMemo(() => {
    return flatBookmarks.filter(b => selectedBookmarks.has(b.id))
  }, [flatBookmarks, selectedBookmarks])

  // Filtered bookmarks for search view
  const filteredBookmarks = useMemo(() => {
    if (!searchQuery.trim()) return flatBookmarks
    return searchBookmarks(flatBookmarks, searchQuery)
  }, [flatBookmarks, searchQuery])

  // Handle curating selected bookmarks as scorables
  const handleCurateSelected = async () => {
    if (!activeProfile) {
      setAlert({ type: 'danger', message: 'Please select a profile first' })
      return
    }

    if (selectedBookmarkObjects.length === 0) {
      setAlert({ type: 'warning', message: 'No bookmarks selected' })
      return
    }

    setCurateLoading(true)
    setCurateProgress({ current: 0, total: selectedBookmarkObjects.length })
    setCurateResults({ success: [], failed: [] })

    const results = { success: [], failed: [] }

    for (let i = 0; i < selectedBookmarkObjects.length; i++) {
      const bookmark = selectedBookmarkObjects[i]
      setCurateProgress({ current: i + 1, total: selectedBookmarkObjects.length })

      try {
        const scoreData = {
          thing: bookmark.url,
          thing_type: 'website',
          score: parseInt(curateScore),
          min_age: curateMinAge ? parseInt(curateMinAge) : null,
          max_age: curateMaxAge ? parseInt(curateMaxAge) : null,
          keywords: curateKeywords ? curateKeywords.split(',').map(k => k.trim()) : [],
          pending: false
        }

        await api.post(`/profiles/${activeProfile.id}/scores`, scoreData)
        results.success.push(bookmark)
      } catch (err) {
        results.failed.push({
          bookmark,
          error: err.response?.data?.message || 'Failed to add'
        })
      }
    }

    setCurateResults(results)
    setCurateLoading(false)

    // Clear successfully added bookmarks from selection
    if (results.success.length > 0) {
      const successIds = new Set(results.success.map(b => b.id))
      setSelectedBookmarks(prev => {
        const next = new Set(prev)
        successIds.forEach(id => next.delete(id))
        return next
      })
    }
  }

  // Close curate modal and show summary
  const closeCurateModal = () => {
    if (curateResults.success.length > 0 || curateResults.failed.length > 0) {
      const successCount = curateResults.success.length
      const failedCount = curateResults.failed.length
      if (successCount > 0 && failedCount === 0) {
        setAlert({ type: 'success', message: `Successfully added ${successCount} bookmark(s) as scorables!` })
      } else if (successCount > 0 && failedCount > 0) {
        setAlert({ type: 'warning', message: `Added ${successCount} bookmark(s). ${failedCount} failed.` })
      } else if (failedCount > 0) {
        setAlert({ type: 'danger', message: `Failed to add ${failedCount} bookmark(s).` })
      }
    }

    setShowCurateModal(false)
    setCurateResults({ success: [], failed: [] })
    setCurateProgress({ current: 0, total: 0 })
  }

  // Render tree node recursively
  const renderTreeNode = (node, depth = 0) => {
    const isExpanded = expandedFolders.has(node.id)
    const paddingLeft = depth * 20

    if (node.type === 'folder') {
      return (
        <div key={node.id}>
          <ListGroup.Item
            action
            onClick={() => toggleFolder(node.id)}
            style={{ paddingLeft: paddingLeft + 12 }}
            className="d-flex align-items-center"
          >
            <span className="me-2" style={{ width: 16 }}>
              {node.children?.length > 0 ? (isExpanded ? '▼' : '▶') : ''}
            </span>
            <span className="me-2">📁</span>
            <span className="flex-grow-1">{node.title}</span>
            <Badge bg="secondary" className="ms-2">
              {node.children?.filter(c => c.type === 'bookmark').length || 0}
            </Badge>
          </ListGroup.Item>
          {isExpanded && node.children?.map(child => renderTreeNode(child, depth + 1))}
        </div>
      )
    }

    if (node.type === 'bookmark') {
      const isSelected = selectedBookmarks.has(node.id)
      return (
        <ListGroup.Item
          key={node.id}
          style={{ paddingLeft: paddingLeft + 12 }}
          className={`d-flex align-items-center ${isSelected ? 'bg-primary bg-opacity-10' : ''}`}
        >
          <Form.Check
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelection(node.id)}
            className="me-2"
          />
          <span className="me-2">🔗</span>
          <div className="flex-grow-1 overflow-hidden">
            <div className="text-truncate fw-medium">{node.title || 'Untitled'}</div>
            <small className="text-muted text-truncate d-block">
              {extractDomain(node.url)}
            </small>
          </div>
        </ListGroup.Item>
      )
    }

    return null
  }

  // Render search result item
  const renderSearchResult = (bookmark) => {
    const isSelected = selectedBookmarks.has(bookmark.id)
    return (
      <ListGroup.Item
        key={bookmark.id}
        className={`d-flex align-items-center ${isSelected ? 'bg-primary bg-opacity-10' : ''}`}
      >
        <Form.Check
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleSelection(bookmark.id)}
          className="me-2"
        />
        <div className="flex-grow-1 overflow-hidden">
          <div className="fw-medium text-truncate">{bookmark.title || 'Untitled'}</div>
          <small className="text-muted d-block text-truncate">{bookmark.url}</small>
          <small className="text-secondary d-block">{bookmark.path}</small>
        </div>
      </ListGroup.Item>
    )
  }

  return (
    <Container className="mt-4">
      <h2>Import Bookmarks</h2>
      {activeProfile && (
        <p className="text-muted">
          Importing as: <strong>{activeProfile.name}</strong>
        </p>
      )}

      {alert && (
        <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {/* File Upload Card */}
      <Card className="mb-4">
        <Card.Body>
          <Card.Title>Upload Bookmarks File</Card.Title>
          <p className="text-muted">
            Export your bookmarks from Chrome, Firefox, or other browsers as HTML and upload them here.
          </p>
          <Form.Group>
            <Form.Control
              type="file"
              accept=".html,.htm"
              onChange={handleFileUpload}
            />
          </Form.Group>
          {parseError && (
            <Alert variant="danger" className="mt-3">{parseError}</Alert>
          )}
          {stats && (
            <div className="mt-3">
              <Badge bg="info" className="me-2">
                {stats.bookmarkCount} bookmarks
              </Badge>
              <Badge bg="secondary">
                {stats.folderCount} folders
              </Badge>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Bookmark Browser */}
      {bookmarkTree && (
        <>
          {/* View Mode Toggle and Search */}
          <Card className="mb-4">
            <Card.Body>
              <Row className="align-items-center mb-3">
                <Col xs={12} md={6}>
                  <div className="btn-group" role="group">
                    <Button
                      variant={viewMode === 'tree' ? 'primary' : 'outline-primary'}
                      onClick={() => setViewMode('tree')}
                    >
                      Tree View
                    </Button>
                    <Button
                      variant={viewMode === 'search' ? 'primary' : 'outline-primary'}
                      onClick={() => setViewMode('search')}
                    >
                      Search View
                    </Button>
                  </div>
                </Col>
                <Col xs={12} md={6} className="mt-2 mt-md-0">
                  <InputGroup>
                    <Form.Control
                      type="text"
                      placeholder="Search bookmarks..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value)
                        if (e.target.value && viewMode === 'tree') {
                          setViewMode('search')
                        }
                      }}
                    />
                    {searchQuery && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSearchQuery('')}
                      >
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>
              </Row>

              {/* Selection Actions */}
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Badge bg="primary" className="py-2 px-3">
                  {selectedBookmarks.size} selected
                </Badge>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={selectAllVisible}
                >
                  Select All Visible
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearSelection}
                  disabled={selectedBookmarks.size === 0}
                >
                  Clear Selection
                </Button>
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => setShowCurateModal(true)}
                  disabled={selectedBookmarks.size === 0 || !activeProfile}
                >
                  Curate as Scorables ({selectedBookmarks.size})
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Tree or Search Results */}
          <Card>
            <Card.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {viewMode === 'tree' ? (
                <ListGroup variant="flush">
                  {bookmarkTree.children?.map(child => renderTreeNode(child, 0))}
                </ListGroup>
              ) : (
                <>
                  {filteredBookmarks.length === 0 ? (
                    <p className="text-muted text-center py-4">
                      {searchQuery ? 'No bookmarks match your search' : 'No bookmarks found'}
                    </p>
                  ) : (
                    <ListGroup variant="flush">
                      {filteredBookmarks.map(renderSearchResult)}
                    </ListGroup>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </>
      )}

      {/* Curate Modal */}
      <Modal show={showCurateModal} onHide={closeCurateModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Curate Bookmarks as Scorables</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!curateLoading && curateProgress.current === 0 && (
            <>
              <Alert variant="info">
                You are about to add <strong>{selectedBookmarkObjects.length}</strong> bookmark(s)
                as scorables to your curation.
              </Alert>

              <Form>
                <Form.Group className="mb-3">
                  <Form.Label>Score for all: {curateScore}</Form.Label>
                  <Form.Range
                    min="0"
                    max="100"
                    value={curateScore}
                    onChange={(e) => setCurateScore(e.target.value)}
                  />
                  <Form.Text className="text-muted">
                    0 = Poor, 50 = Average, 100 = Excellent
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Minimum Age (optional)</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="e.g., 13"
                        value={curateMinAge}
                        onChange={(e) => setCurateMinAge(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Maximum Age (optional)</Form.Label>
                      <Form.Control
                        type="number"
                        placeholder="e.g., 18"
                        value={curateMaxAge}
                        onChange={(e) => setCurateMaxAge(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Keywords (comma-separated, applies to all)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="education, reference, tools"
                    value={curateKeywords}
                    onChange={(e) => setCurateKeywords(e.target.value)}
                  />
                </Form.Group>
              </Form>

              <h6>Selected Bookmarks:</h6>
              <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                <ListGroup variant="flush" className="small">
                  {selectedBookmarkObjects.map(b => (
                    <ListGroup.Item key={b.id} className="py-1">
                      <div className="text-truncate">{b.title}</div>
                      <small className="text-muted text-truncate d-block">{b.url}</small>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            </>
          )}

          {curateLoading && (
            <div className="text-center py-4">
              <Spinner animation="border" className="mb-3" />
              <div>
                Adding bookmark {curateProgress.current} of {curateProgress.total}...
              </div>
              <div className="progress mt-3">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ width: `${(curateProgress.current / curateProgress.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {!curateLoading && (curateResults.success.length > 0 || curateResults.failed.length > 0) && (
            <>
              {curateResults.success.length > 0 && (
                <Alert variant="success">
                  Successfully added {curateResults.success.length} bookmark(s) as scorables!
                </Alert>
              )}
              {curateResults.failed.length > 0 && (
                <>
                  <Alert variant="danger">
                    Failed to add {curateResults.failed.length} bookmark(s):
                  </Alert>
                  <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                    <ListGroup variant="flush" className="small">
                      {curateResults.failed.map((item, idx) => (
                        <ListGroup.Item key={idx} className="py-1 text-danger">
                          <div className="text-truncate">{item.bookmark.title}</div>
                          <small>{item.error}</small>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCurateModal}>
            {curateResults.success.length > 0 || curateResults.failed.length > 0 ? 'Close' : 'Cancel'}
          </Button>
          {!curateLoading && curateProgress.current === 0 && (
            <Button
              variant="primary"
              onClick={handleCurateSelected}
              disabled={selectedBookmarkObjects.length === 0}
            >
              Add {selectedBookmarkObjects.length} Bookmark(s)
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default BookmarkImport
